import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

/**
 * Hook for admin dashboard data management
 */
export function useAdminData() {
  const [analytics, setAnalytics] = useState(null);
  const [clients, setClients] = useState([]);
  const [smsConfig, setSmsConfig] = useState({});
  const [ussdConfig, setUssdConfig] = useState({});
  const [otpSessions, setOtpSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, clientsRes, configRes, ussdConfigRes, sessionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/analytics`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/clients`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/sms-config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/ussd-config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/otp-sessions?limit=100`, { withCredentials: true }),
      ]);
      setAnalytics(analyticsRes.data);
      setClients(clientsRes.data);
      setSmsConfig(configRes.data);
      setUssdConfig(ussdConfigRes.data);
      setOtpSessions(sessionsRes.data.sessions || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const createClient = useCallback(async (formData) => {
    const response = await axios.post(`${API_URL}/api/admin/clients`, formData, { withCredentials: true });
    return response.data;
  }, []);

  const updateClient = useCallback(async (clientId, formData) => {
    const response = await axios.put(`${API_URL}/api/admin/clients/${clientId}`, formData, { withCredentials: true });
    return response.data;
  }, []);

  const deleteClient = useCallback(async (clientId) => {
    await axios.delete(`${API_URL}/api/admin/clients/${clientId}`, { withCredentials: true });
  }, []);

  const updateSmsConfig = useCallback(async (formData) => {
    const response = await axios.put(`${API_URL}/api/admin/sms-config`, formData, { withCredentials: true });
    return response.data;
  }, []);

  return {
    analytics,
    clients,
    smsConfig,
    ussdConfig,
    otpSessions,
    loading,
    fetchData,
    createClient,
    updateClient,
    deleteClient,
    updateSmsConfig,
  };
}

/**
 * Hook for client dashboard data management
 */
export function useClientData() {
  const [analytics, setAnalytics] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [otpSessions, setOtpSessions] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, keysRes, sessionsRes, usageRes] = await Promise.all([
        axios.get(`${API_URL}/api/client/analytics`, { withCredentials: true }),
        axios.get(`${API_URL}/api/client/api-keys`, { withCredentials: true }),
        axios.get(`${API_URL}/api/client/otp-sessions?limit=50`, { withCredentials: true }),
        axios.get(`${API_URL}/api/client/usage`, { withCredentials: true }),
      ]);
      setAnalytics(analyticsRes.data);
      setApiKeys(keysRes.data);
      setOtpSessions(sessionsRes.data.sessions || []);
      setUsage(usageRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const createApiKey = useCallback(async () => {
    const response = await axios.post(`${API_URL}/api/client/api-keys`, {}, { withCredentials: true });
    return response.data;
  }, []);

  const revokeApiKey = useCallback(async (keyId) => {
    await axios.delete(`${API_URL}/api/client/api-keys/${keyId}`, { withCredentials: true });
  }, []);

  return {
    analytics,
    apiKeys,
    otpSessions,
    usage,
    loading,
    fetchData,
    createApiKey,
    revokeApiKey,
  };
}
